import { useEffect, useMemo, useState } from 'react';
import type { HITLRendererProps } from '../HITLRenderer';
import { hitlStyles, cn } from './styles';
import type {
  HITLAutoFix,
  HITLFieldError,
  HITLFormField,
  HITLFormValidationPayload,
} from '@shared/types/hitl';
import { isHITLSubmitResult } from '@shared/types/hitl';

const isTechnicalCopy = (value: string): boolean => /rfc3339|iana|json|invalid_/i.test(value);

const toDisplayHelpText = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const text = value.trim();
  if (!text || isTechnicalCopy(text)) return undefined;
  return text;
};

const formatAutoFixMessage = (fix: HITLAutoFix): string => {
  if (typeof fix.before === 'string' && typeof fix.after === 'string') {
    return `${fix.field}: "${fix.before}" → "${fix.after}"`;
  }
  return `${fix.field}: valeur ajustée automatiquement.`;
};

export const FormRenderer: React.FC<HITLRendererProps> = ({
  response,
  onSubmit,
  onCancel,
  isSubmitting: externalSubmitting,
  compact,
}) => {
  const [formData, setFormData] = useState<Record<string, unknown>>(response.formData || {});
  const [fieldErrors, setFieldErrors] = useState<Record<string, HITLFieldError>>(
    response.formValidation?.fieldErrors ?? {},
  );
  const [autoFixes, setAutoFixes] = useState<HITLAutoFix[]>(
    response.formValidation?.autoFixes ?? [],
  );
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);

  const isSubmitting = externalSubmitting || isLocalSubmitting;
  const hasValidationErrors = Object.keys(fieldErrors).length > 0;

  useEffect(() => {
    setFormData(response.formData || {});
    setFieldErrors(response.formValidation?.fieldErrors ?? {});
    setAutoFixes(response.formValidation?.autoFixes ?? []);
    setIsLocalSubmitting(false);
  }, [response.formData, response.formValidation, response.stepId, response.questionId]);

  // Generate schema from data if not provided
  const schema = useMemo(() => {
    if (response.formSchema) return response.formSchema;

    // Auto-generate schema from formData
    const autoSchema: Record<string, HITLFormField> = {};
    Object.entries(formData).forEach(([key, value]) => {
      const strValue = String(value || '');
      const isLongText = strValue.length > 60 ||
        key.toLowerCase().includes('description') ||
        key.toLowerCase().includes('message') ||
        key.toLowerCase().includes('note');

      autoSchema[key] = {
        type: isLongText ? 'textarea' : 'text',
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
      };
    });
    return autoSchema;
  }, [response.formSchema, formData]);

  const applyValidationFeedback = (validation: HITLFormValidationPayload) => {
    if (validation.normalizedData) {
      setFormData((previous) => ({ ...previous, ...validation.normalizedData }));
    }
    setFieldErrors(validation.fieldErrors ?? {});
    setAutoFixes(validation.autoFixes ?? []);
  };

  const handleChange = (key: string, value: unknown) => {
    setFormData((previous) => {
      if (value === undefined) {
        const next = { ...previous };
        delete next[key];
        return next;
      }
      return { ...previous, [key]: value };
    });
    setFieldErrors((previous) => {
      if (!previous[key]) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async () => {
    setIsLocalSubmitting(true);
    try {
      const submitResult = await onSubmit(formData);
      if (isHITLSubmitResult(submitResult) && submitResult.ok === false) {
        if (submitResult.validation) {
          applyValidationFeedback(submitResult.validation);
        }
        setIsLocalSubmitting(false);
        return;
      }
    } catch (error) {
      console.error('HITL form submit failed:', error);
      setIsLocalSubmitting(false);
    }
  };

  const renderField = (key: string, field: HITLFormField) => {
    const value = formData[key];
    const error = fieldErrors[key];
    const baseFieldClass = cn(
      field.type === 'textarea' ? hitlStyles.textarea : hitlStyles.input,
      error ? 'border-red-400 focus:ring-red-500' : undefined,
    );
    const commonProps = {
      placeholder: field.placeholder,
      disabled: isSubmitting,
      minLength: field.minLength,
      maxLength: field.maxLength,
      pattern: field.pattern,
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={String(value || '')}
            onChange={(e) => handleChange(key, e.target.value)}
            {...commonProps}
            rows={3}
            className={cn(baseFieldClass, 'resize-y')}
          />
        );

      case 'select':
        return (
          <select
            value={String(value || '')}
            onChange={(e) => handleChange(key, e.target.value)}
            disabled={isSubmitting}
            className={baseFieldClass}
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type={field.type === 'email'
              ? 'email'
              : field.type === 'url'
              ? 'url'
              : field.type === 'number'
              ? 'number'
              : 'text'}
            value={String(value || '')}
            onChange={(e) => handleChange(
              key,
              field.type === 'number'
                ? (e.target.value === '' ? undefined : e.target.valueAsNumber)
                : e.target.value,
            )}
            min={field.minimum}
            max={field.maximum}
            {...commonProps}
            className={baseFieldClass}
          />
        );
    }
  };

  return (
    <div className={compact ? hitlStyles.containerCompact : hitlStyles.container}>
      {/* Header */}
      {(response.formTitle || response.message) && (
        <div className="mb-4 pb-3 border-b border-domino-cardBorder">
          {response.formTitle && (
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base">⚡️</span>
              <h3 className="text-sm font-semibold text-domino-aubergine">
                {response.formTitle}
              </h3>
            </div>
          )}
          {(response.formDescription || response.message) && (
            <p className="text-sm text-domino-prune">
              {response.formDescription || response.message}
            </p>
          )}
        </div>
      )}

      {autoFixes.length > 0 && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
          <p className="text-xs font-medium text-emerald-700 mb-1">
            {autoFixes.length} correction{autoFixes.length > 1 ? 's' : ''} automatique{autoFixes.length > 1 ? 's' : ''} appliquée{autoFixes.length > 1 ? 's' : ''}.
          </p>
          <ul className="space-y-1">
            {autoFixes.slice(0, 3).map((fix, index) => (
              <li key={`${fix.field}-${fix.rule}-${index}`} className="text-xs text-emerald-700">
                {formatAutoFixMessage(fix)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Form Fields */}
      <div className="space-y-3 mb-4">
        {Object.entries(schema).map(([key, field]) => (
          <div key={key} className={hitlStyles.inputGroup}>
            <label className={hitlStyles.label}>
              {field.label || key}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderField(key, field)}
            {(toDisplayHelpText(field.helpText) || (field.examples && field.examples.length > 0)) && (
              <p className="mt-1 text-xs text-domino-prune">
                {toDisplayHelpText(field.helpText)}
                {field.examples && field.examples.length > 0 && (
                  <>
                    {toDisplayHelpText(field.helpText) ? ' ' : ''}
                    Exemple: {field.examples[0]}
                  </>
                )}
              </p>
            )}
            {fieldErrors[key] && (
              <p className="mt-1 text-xs text-red-700">
                {fieldErrors[key].message}
                {fieldErrors[key].suggestedValue !== undefined && (
                  <> (Suggestion: {String(fieldErrors[key].suggestedValue)})</>
                )}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className={cn(hitlStyles.actionButton, hitlStyles.actionButtonSecondary)}
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={cn(hitlStyles.actionButton, hitlStyles.actionButtonPrimary)}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className={hitlStyles.spinner} />
              {hasValidationErrors ? 'Validation...' : 'Applying...'}
            </span>
          ) : (
            hasValidationErrors ? 'Valider et continuer' : 'Confirm & Execute'
          )}
        </button>
      </div>
    </div>
  );
};
